import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UsernameStep } from "./onboarding/UsernameStep";
import { TutorialStep } from "./onboarding/TutorialStep";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTransferPreAuthMetrics } from "@/hooks/useTransferPreAuthMetrics";
import { useTheme } from "@/providers/ThemeProvider";
import { isNativeApp } from "@/lib/platform";
import { loginToRevenueCat } from "@/lib/purchases";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export type OnboardingStep = "username" | "tutorial";

export const OnboardingFlow = ({ userId }: { userId: string }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("username");
  
  // Transfer any metrics created before authentication
  useTransferPreAuthMetrics(userId);

  /**
   * Associate a pending RevenueCat purchase (made anonymously before signup)
   * with the now-authenticated user account.
   */
  const associatePendingPurchase = async () => {
    const hasPending = localStorage.getItem('pending_revenuecat_association');
    if (!hasPending || !isNativeApp()) return;

    console.log('[OnboardingFlow] Associating pending RevenueCat purchase with user:', userId);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const email = sessionData.session?.user?.email || '';

      const loginResult = await loginToRevenueCat(userId, email, supabase);
      if (loginResult.success) {
        console.log('[OnboardingFlow] RevenueCat login successful:', loginResult.customerId);
        
        // Trigger subscription check to update premium status
        await supabase.functions.invoke("check-subscription", {
          headers: {
            Authorization: `Bearer ${sessionData.session?.access_token}`,
          },
        });

        console.log('[OnboardingFlow] Subscription check completed');
        toast.success(t('paywall.subscription_activated'));
      } else {
        console.error('[OnboardingFlow] RevenueCat login failed:', loginResult.error);
        // Don't block onboarding - they can restore later on /upgrade
        toast.error(t('paywall.link_failed'), {
          duration: 6000,
        });
      }
    } catch (error) {
      console.error('[OnboardingFlow] Error associating purchase:', error);
    } finally {
      localStorage.removeItem('pending_revenuecat_association');
    }
  };

  const handleUsernameComplete = async () => {
    console.log("[OnboardingFlow] Username complete - checking for pending purchases");
    
    // Associate any pending RevenueCat purchase before redirecting
    await associatePendingPurchase();
    
    // Mark onboarding as complete and go straight to dashboard
    localStorage.setItem("onboarding_complete", "true");
    
    // Set flag to prevent Index from redirecting back (will be cleared by Index)
    sessionStorage.setItem('just_completed_username_setup', 'true');
    
    // Clear the pending Google OAuth flag now that username setup is complete
    sessionStorage.removeItem('pending_google_oauth');
    console.log("[OnboardingFlow] Cleared pending_google_oauth flag");
    
    // Redirect mobile users to /track page, desktop users to home page
    if (isMobile) {
      console.log("[OnboardingFlow] Mobile user - redirecting to /track");
      navigate("/track", { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  const handleTutorialComplete = () => {
    console.log("[OnboardingFlow] Tutorial complete - marking onboarding complete");
    // After tutorial, mark onboarding as complete and go to dashboard
    // Pro upsell will be shown on third AI quick entry button press
    localStorage.setItem("onboarding_complete", "true");
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {currentStep === "username" && (
        <UsernameStep userId={userId} onComplete={handleUsernameComplete} />
      )}
      {currentStep === "tutorial" && !isMobile && (
        <TutorialStep onComplete={handleTutorialComplete} />
      )}
    </div>
  );
};
